/**
 * VIB-3030: kundevalg i fakturadialogen. Prosjektet har én hovedkunde, men en
 * faktura kan gå til en annen kunde. `invoices.customer_id` NULL betyr
 * prosjektets kunde.
 */

export interface InvoiceCustomerOption {
  id: string;
  name: string;
  organization_number?: string | null;
  poweroffice_customer_id: number | string | null;
}

export interface InvoiceCustomerGroups<C extends InvoiceCustomerOption> {
  main: C | null;
  /** Kunder brukt på tidligere fakturaer i prosjektet, med antall. */
  used: { customer: C; invoiceCount: number }[];
  /** Søketreff i hele kundelista, uten hovedkunden og de brukte. */
  matches: C[];
}

const MAX_MATCHES = 8;

/** Hovedkunden, kunder brukt på prosjektet, så søk i alle kunder. */
export function invoiceCustomerGroups<C extends InvoiceCustomerOption>(input: {
  mainCustomerId: string | null;
  customers: readonly C[];
  projectInvoices: ReadonlyArray<{ customer_id: string | null }>;
  query: string;
}): InvoiceCustomerGroups<C> {
  const byId = new Map(input.customers.map((c) => [c.id, c]));
  const main = input.mainCustomerId ? byId.get(input.mainCustomerId) ?? null : null;

  const counts = new Map<string, number>();
  for (const inv of input.projectInvoices) {
    if (!inv.customer_id || inv.customer_id === input.mainCustomerId) continue;
    counts.set(inv.customer_id, (counts.get(inv.customer_id) ?? 0) + 1);
  }
  const q = input.query.trim().toLowerCase();
  const matchesQuery = (c: C) =>
    !q || c.name.toLowerCase().includes(q) || (c.organization_number ?? "").replace(/\s/g, "").includes(q.replace(/\s/g, ""));

  const used = [...counts.entries()]
    .map(([id, invoiceCount]) => ({ customer: byId.get(id), invoiceCount }))
    .filter((u): u is { customer: C; invoiceCount: number } => !!u.customer && matchesQuery(u.customer))
    .sort((a, b) => a.customer.name.localeCompare(b.customer.name, "nb-NO"));

  const taken = new Set([input.mainCustomerId, ...counts.keys()]);
  const matches = q
    ? input.customers.filter((c) => !taken.has(c.id) && matchesQuery(c)).slice(0, MAX_MATCHES)
    : [];

  return { main, used, matches };
}

/**
 * Kunden en kreditnota må gå til: kunden på fakturaen den krediterer.
 * `undefined` når kreditnotaen ikke peker på en kjent faktura.
 */
export function creditNoteCustomerId(
  relatedInvoiceId: string | null | undefined,
  projectInvoices: ReadonlyArray<{ id: string; customer_id: string | null }>,
  mainCustomerId: string | null,
): string | null | undefined {
  if (!relatedInvoiceId) return undefined;
  const original = projectInvoices.find((i) => i.id === relatedInvoiceId);
  if (!original) return undefined;
  return original.customer_id ?? mainCustomerId;
}

/** Kunden fakturaen gikk til: lagret kunde, ellers prosjektets kunde. */
export function invoiceCustomerId(
  invoice: { customer_id: string | null },
  projectCustomerId: string | null | undefined,
): string | null {
  return invoice.customer_id ?? projectCustomerId ?? null;
}
